import { useEffect, useMemo, useState } from "react";
import { fetchPlayoffsData } from "./usePlayoffsApi";

/**
 * Parses a resultadoPartido string like "Hebraica Macabi 73 - Aguada 78"
 * into structured score data. The team name may contain spaces.
 * Returns { local: bool, scoreAguada: number, scoreRival: number } or null.
 */
function parseResultadoPartido(str) {
    if (!str) return null;

    const parts = str.split(' - ');
    if (parts.length !== 2) return null;

    const parseHalf = (s) => {
        const match = s.trim().match(/^(.+?)\s+(\d+)$/);
        if (!match) return null;
        return { team: match[1].trim(), score: parseInt(match[2], 10) };
    };

    const left = parseHalf(parts[0]);
    const right = parseHalf(parts[1]);
    if (!left || !right) return null;

    const aguadaIsLocal = left.team === 'Aguada';
    return {
        local: aguadaIsLocal,
        scoreAguada: aguadaIsLocal ? left.score : right.score,
        scoreRival: aguadaIsLocal ? right.score : left.score,
    };
}

function normalizeSerie(raw) {
    const lastGame = raw.evolucion?.length
        ? raw.evolucion[raw.evolucion.length - 1]
        : null;
    const rawFinal = raw.resultadoFinal ?? lastGame?.estadoSerie ?? '0-0';
    const resultadoParts = rawFinal.split('-');
    const aguadaGames = parseInt(resultadoParts[0], 10) || 0;
    const rivalGames = parseInt(resultadoParts[1], 10) || 0;

    return {
        _id: raw._id,
        temporada: raw.liga || '-',
        ronda: raw.etapa || '-',
        resultado: raw.ganado == null ? 'en-curso' : raw.ganado ? 'ganada' : 'perdida',
        aguadaScore: aguadaGames,
        rivalScore: rivalGames,
        aguadaPosicion: raw.posicionAguada != null ? `${raw.posicionAguada}°` : '-',
        aguadaPosicionNum: raw.posicionAguada ?? null,
        rival: raw.adversario || '-',
        rivalPosicion: raw.posicionAdversario != null ? `${raw.posicionAdversario}°` : '-',
        rivalPosicionNum: raw.posicionAdversario ?? null,
        alMejorDe: raw.alMejorDe,
        comentarios: raw.comentarios || null,
        partidos: (raw.evolucion || []).map((e) => {
            const parsed = parseResultadoPartido(e.resultadoPartido);
            return {
                numero: e.juego,
                estadoSerie: e.estadoSerie,
                ganado: e.ganado,
                cancha: e.cancha || null,
                local: parsed?.local ?? true,
                scoreAguada: parsed?.scoreAguada ?? 0,
                scoreRival: parsed?.scoreRival ?? 0,
                label: e.resultadoPartido || '',
                partidoId: e.partidoId || null,
            };
        }),
    };
}

// Cached across the whole browser tab session so remounts (e.g. back navigation
// from a partido) reuse the data instead of refetching from the API.
let playoffsCache = null;
let playoffsCachePromise = null;

function loadPlayoffsData() {
    if (playoffsCache) {
        return Promise.resolve(playoffsCache);
    }

    if (!playoffsCachePromise) {
        playoffsCachePromise = fetchPlayoffsData()
            .then((data) => {
                playoffsCache = data;
                return data;
            })
            .catch((error) => {
                playoffsCachePromise = null;
                throw error;
            });
    }

    return playoffsCachePromise;
}

function usePlayoffsData() {
    const [isLoading, setIsLoading] = useState(!playoffsCache);
    const [isError, setIsError] = useState(false);
    const [series, setSeries] = useState(() =>
        playoffsCache ? (Array.isArray(playoffsCache) ? playoffsCache : []).map(normalizeSerie) : []
    );

    useEffect(() => {
        let mounted = true;
        // Avoid a redundant setSeries (new array reference) when the initial
        // state already picked up the cached data synchronously above.
        const hadCacheAtMount = Boolean(playoffsCache);

        if (!hadCacheAtMount) {
            setIsLoading(true);
        }
        setIsError(false);

        loadPlayoffsData()
            .then((data) => {
                if (!mounted || hadCacheAtMount) return;
                const normalized = (Array.isArray(data) ? data : []).map(normalizeSerie);
                setSeries(normalized);
            })
            .catch(() => {
                if (!mounted) return;
                setIsError(true);
            })
            .finally(() => {
                if (!mounted) return;
                setIsLoading(false);
            });

        return () => { mounted = false; };
    }, []);

    const temporadas = useMemo(() => {
        const unique = [...new Set(series.map((s) => s.temporada))];
        return ['Todas las temporadas', ...unique];
    }, [series]);

    const efectividad = useMemo(() => {
        if (!series.length) return 0;
        const ganadas = series.filter((s) => s.resultado === 'ganada').length;
        return Math.round((ganadas / series.length) * 100);
    }, [series]);

    return { isLoading, isError, series, temporadas, efectividad };
}

export default usePlayoffsData;
